import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Globe, Mail, Phone, ChevronDown } from 'lucide-react';
import logoWhite from '../../assets/images/white-logo-roundvoip.svg';
import logoMark from '../../assets/images/logo-roundvoip.svg';
import './AuthLayout.css';

const CONTACT = {
  phone: '01 76 23 42 34',
  email: 'serviceclient@roundesk.com',
  website: 'www.roundesk.com',
};

export default function AuthLayout() {
  const { t } = useTranslation();

  return (
    <div className="auth-shell">
      <aside className="auth-aside">
        <div className="auth-aside__inner">
          <div className="auth-brand">
            <img src={logoWhite} alt={t('auth.brandName')} className="auth-brand__logo" />
          </div>

          <div className="auth-pitch">
            <h1 className="auth-pitch__title">
              {t('auth.pitchTitle')}
              <br />
              <span className="auth-pitch__title-soft">{t('auth.pitchTitleAccent')}</span>
            </h1>
            <p className="auth-pitch__desc">{t('auth.pitchDesc')}</p>

            <ul className="auth-features">
              {['auth.feature1', 'auth.feature2', 'auth.feature3'].map((key) => (
                <li key={key} className="auth-features__item">
                  <span className="auth-features__icon" aria-hidden="true">
                    <Check size={11} strokeWidth={3} />
                  </span>
                  {t(key)}
                </li>
              ))}
            </ul>
          </div>

          <div className="auth-help">
            <h2 className="auth-help__title">{t('auth.helpTitle')}</h2>
            <ul className="auth-help__list">
              <li className="auth-help__item">
                <Phone size={12} aria-hidden="true" />
                <a className="auth-help__link" href={`tel:${CONTACT.phone.replace(/\s/g, '')}`}>
                  {CONTACT.phone}
                </a>
              </li>
              <li className="auth-help__item">
                <Mail size={12} aria-hidden="true" />
                <a className="auth-help__link" href={`mailto:${CONTACT.email}`}>
                  {CONTACT.email}
                </a>
              </li>
              <li className="auth-help__item">
                <Globe size={12} aria-hidden="true" />
                <a
                  className="auth-help__link"
                  href={`https://${CONTACT.website}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {CONTACT.website}
                </a>
              </li>
            </ul>
          </div>

          <p className="auth-legal">
            &copy; {new Date().getFullYear()} {t('auth.brandName')} &bull; SOC 2 &bull; RGPD
          </p>
        </div>
      </aside>

      <main className="auth-main">
        <LanguageSelector />
        <div className="auth-main__content">
          <div className="auth-mobile-brand">
            <img src={logoMark} alt={t('auth.brandName')} className="auth-mobile-brand__logo" />
          </div>
          <Outlet />
        </div>
      </main>
    </div>
  );
}

const LANGUAGES = [
  { code: 'fr', name: 'Français' },
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
];

function LanguageSelector() {
  const { i18n, t } = useTranslation();
  const current = LANGUAGES.find((l) => i18n.language.startsWith(l.code)) ?? LANGUAGES[0];

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
  };

  return (
    <div className="auth-lang">
      <div className="auth-lang__control">
        <Globe size={14} aria-hidden="true" />
        <span className="auth-lang__value">{current.name}</span>
        <ChevronDown size={13} aria-hidden="true" />
        <select
          className="auth-lang__select"
          aria-label={t('auth.languageLabel')}
          value={current.code}
          onChange={(e) => changeLanguage(e.target.value)}
        >
          {LANGUAGES.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
